import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChatBubbleLeftRightIcon } from '@heroicons/react/24/solid';
import { chatAPI } from '../services/api';
import { useSocket } from '../hooks/useSocket';

const FloatingChatButton = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);

  // Get user info from localStorage
  const isStudentPortal = location.pathname.startsWith('/student');
  const token = localStorage.getItem(isStudentPortal ? 'studentAuthToken' : 'authToken');
  const userStr = localStorage.getItem(isStudentPortal ? 'studentUser' : 'user');

  let user = null;
  try {
    user = userStr ? JSON.parse(userStr) : null;
  } catch (e) {
    console.error('Error parsing user data:', e);
  }

  const userId = user?._id || user?.id;
  const userType = isStudentPortal ? 'student' : 'user';
  const organizationId = user?.organization_id;

  // Initialize WebSocket for real-time updates
  const { onNewMessage, off } = useSocket(token, userId, userType, organizationId);

  // Fetch unread count function
  const fetchUnreadCount = useCallback(async () => {
    try {
      const endpoint = isStudentPortal
        ? chatAPI.getStudentConversations(100, 0)
        : chatAPI.getConversations(100, 0);

      const response = await endpoint;
      const conversations = response.data.conversations;

      // Sum up unread counts for current user
      const totalUnread = conversations.reduce((total, conv) => {
        return total + (conv.unread_count[userId] || 0);
      }, 0);

      setUnreadCount(totalUnread);
    } catch (error) {
      console.error('Error fetching unread count:', error);
      // If 401, user might not be authenticated yet, just set to 0
      if (error.response?.status === 401) {
        setUnreadCount(0);
      }
    }
  }, [isStudentPortal, userId]);

  // Fetch unread count on mount
  useEffect(() => {
    if (token && userId) {
      fetchUnreadCount();
    }
  }, [token, userId, fetchUnreadCount]);

  // Listen for new messages to update count
  useEffect(() => {
    if (!userId) return;

    const handleNewMessage = (message) => {
      // Only increment if we're not on the chat page
      const onChatPage = location.pathname === '/chat' || location.pathname === '/student/chat';
      if (!onChatPage && message.sender_id !== userId) {
        setUnreadCount(prev => prev + 1);
      }
    };

    onNewMessage(handleNewMessage);

    return () => {
      off('new_message');
    };
  }, [userId, location.pathname, onNewMessage, off]);

  // Don't show the button if we're already on the chat page
  if (location.pathname === '/chat' || location.pathname === '/student/chat') {
    return null;
  }

  const handleClick = () => {
    // Reset unread count when opening chat
    setUnreadCount(0);

    // Navigate to appropriate chat route based on current path
    if (location.pathname.startsWith('/student')) {
      navigate('/student/chat');
    } else {
      navigate('/chat');
    }
  };

  return (
    <div className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-[9999]">
      <button
        onClick={handleClick}
        className="bg-gradient-to-br from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 active:scale-95 text-white p-3.5 sm:p-4 rounded-full shadow-2xl hover:scale-110 transition-all duration-300 group relative min-h-[56px] min-w-[56px] flex items-center justify-center"
        title={unreadCount > 0 ? `${unreadCount} unread message${unreadCount > 1 ? 's' : ''}` : 'Open Chat'}
        aria-label={unreadCount > 0 ? `Open chat. ${unreadCount} unread messages` : 'Open chat'}
      >
        <ChatBubbleLeftRightIcon className="h-6 w-6 sm:h-7 sm:w-7 group-hover:rotate-12 transition-transform" />

        {/* Notification Badge */}
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full min-w-[20px] h-5 px-1.5 flex items-center justify-center animate-pulse shadow-lg">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>
    </div>
  );
};

export default FloatingChatButton;
